import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { skipToken } from '@reduxjs/toolkit/query'
import { startOfDay } from 'date-fns'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { isV2AiConversation } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/conversations'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { CONVERSATION_STATE, CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { AIConversationToggleRow } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/aiCoachToggle/AICoachToggle'

import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { useAppDispatch } from '../../../../../data/store'
import { setToastContent } from '../../../../data/ltToastAutoActions'
import { usePatientConversationsData } from '../../../../messages/data/hooks/useConversationsData'
import { usePatchConversationMutation } from '../../../../messages/data/messagesApi'
import { getAiConversationMixpanelProperties } from '../../../../messages/data/mixpanelUtils'
import {
  AI_CONVERSATION_TOGGLE_TYPES,
  getAiConversationByType,
  getManagerCoachingLicense,
  isLicenseExpired,
  shouldShowAiConversationToggle,
} from '../aiConversationToggleRows'

const AI_CHAT_TOGGLE_ERROR_TOAST = {
  text: 'Failed to update AI chat status',
  toastType: 'error',
  id: 'AIChatToggle-toast-error',
} as const

/**
 * Status updates still go through the V1 endpoint with the Twilio conversation ID, so V2 Lyra AI
 * conversations must patch by their external ID. Mirrors the messenger's status-patch handling.
 */
const getStatusPatchConversationId = (conversation: ConversationObject): string | null =>
  isV2AiConversation(conversation) ? (conversation.external_conversation_id ?? null) : conversation.conversation_id

type UseAiConversationToggleRowsArgs = {
  currentEpisode: Episode | undefined
  clientDetails: Partial<Pick<ClientObject, 'seat_based_licenses'>> | undefined
  clientId: string | null | undefined
  providerId: string
  skip?: boolean
}

/**
 * Builds the rows for the "Conversations with Lyra AI" card on the client profile. Each visible AI
 * conversation becomes one ON/OFF row; changing a row PATCHes that conversation's status. Manager
 * growth coaching rows also carry the client's seat license end date.
 */
export const useAiConversationToggleRows = ({
  currentEpisode,
  clientDetails,
  clientId,
  providerId,
  skip = false,
}: UseAiConversationToggleRowsArgs): AIConversationToggleRow[] => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const [patchConversation] = usePatchConversationMutation()
  const { isAiCoachEnabled } = useFlags()
  const { currentData: aiConversations } = usePatientConversationsData(
    clientId && isAiCoachEnabled && !skip
      ? { patientId: clientId, providerId, types: CONVERSATION_TYPE.AI_COACH }
      : skipToken,
  )

  const setConversationStatus = useCallback(
    (conversation: ConversationObject, isOn: boolean) => {
      const newStatus = isOn ? CONVERSATION_STATE.ACTIVE : CONVERSATION_STATE.INACTIVE
      if (!clientId || conversation.conversation_state === newStatus) {
        return
      }
      const statusPatchConversationId = getStatusPatchConversationId(conversation)
      if (!statusPatchConversationId) {
        dispatch(setToastContent(AI_CHAT_TOGGLE_ERROR_TOAST))
        return
      }
      track({
        event: mixpanelEvents.TOGGLE_GIVE_ACCESS_TO_LYRA_AI,
        properties: getAiConversationMixpanelProperties(conversation.conversation_attributes?.type),
        details: {
          status: newStatus,
        },
      })
      patchConversation({
        conversationId: statusPatchConversationId,
        cacheConversationId: conversation.conversation_id,
        isV2: isV2AiConversation(conversation),
        status: newStatus,
        patientId: clientId,
        providerId,
      })
        .unwrap()
        .catch(() => {
          dispatch(setToastContent(AI_CHAT_TOGGLE_ERROR_TOAST))
        })
    },
    [clientId, dispatch, patchConversation, providerId],
  )

  const managerCoachingLicense = getManagerCoachingLicense(clientDetails)
  // Expiry is day-granular, so key the memo on the calendar day rather than the instant. This keeps
  // the rows stable across re-renders yet re-evaluates `isExpired` once a render lands on a new day.
  const today = startOfDay(new Date()).getTime()

  return useMemo(() => {
    const now = new Date(today)
    return AI_CONVERSATION_TOGGLE_TYPES.flatMap((type): AIConversationToggleRow[] => {
      const conversation = getAiConversationByType(aiConversations, type)
      if (!conversation || !shouldShowAiConversationToggle(conversation, currentEpisode)) {
        return []
      }
      const isManagerCoaching = type === CONVERSATION_TYPE.MANAGER_COACHING_AI
      const license = isManagerCoaching ? managerCoachingLicense : undefined
      return [
        {
          id: type,
          label: isManagerCoaching
            ? formatMessage({
                defaultMessage: 'Manager growth coaching',
                description: 'Label for the manager growth coaching Lyra AI conversation toggle',
              })
            : formatMessage({
                defaultMessage: 'Mental health coaching',
                description: 'Label for the mental health coaching Lyra AI conversation toggle',
              }),
          isOn: conversation.conversation_state === CONVERSATION_STATE.ACTIVE,
          onChange: (isOn: boolean) => setConversationStatus(conversation, isOn),
          expirationDate: license?.access_end_date,
          isExpired: license ? isLicenseExpired(license, now) : false,
        },
      ]
    })
  }, [aiConversations, currentEpisode, formatMessage, managerCoachingLicense, setConversationStatus, today])
}
